import React from "react";
import {
  Box, Drawer, Tooltip, Accordion, AccordionSummary, AccordionDetails,
  Typography, IconButton, CircularProgress,
  Table, TableHead, TableBody, TableRow, TableCell,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import clsx from "clsx";
import {
  CloseRounded as CloseRoundedIcon,
  ExpandMore as ExpandMoreIcon,
  InfoOutlined as InfoOutlinedIcon,
  DescriptionOutlined as DescriptionOutlinedIcon,
  GridViewOutlined as GridViewOutlinedIcon,
  PlaceOutlined as PlaceOutlinedIcon,
  ListOutlined as ListOutlinedIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import agent from "../../../../agent/agent";
import { getFileTypeIcon } from "../../../../icons/FileTypeIcons";
import formatFileSize from "../../../../utils/formatFileSize";
import formatDate from "../../../../utils/formatDate";
import truncateFilename from "../../../../utils/truncateFilename";
import downloadFile from "../../../../utils/downloadFile";
import { LocationGoLink, IconGoLink } from "../../../../components/SectionGoButtons";
import {
  getCameraToAddressesUrl,
  getSensorToAddressesUrl,
} from "../../../../utils/sectionNavigation";
import {
  formatAddressDisplay,
  getCameraLocationLabel,
  getComplexLocationLabel,
} from "../../../../utils/addressFields";

const CloudDownloadIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g clipPath="url(#clip0_906_17595)">
      <path d="M11.9248 13.6498L10.8332 14.6998V9.99984C10.8332 9.77882 10.7454 9.56686 10.5891 9.41058C10.4328 9.2543 10.2208 9.1665 9.99983 9.1665C9.77882 9.1665 9.56685 9.2543 9.41057 9.41058C9.25429 9.56686 9.1665 9.77882 9.1665 9.99984V14.6582L8.0915 13.5748C8.0138 13.4971 7.92156 13.4355 7.82004 13.3935C7.71852 13.3514 7.60971 13.3298 7.49983 13.3298C7.38995 13.3298 7.28114 13.3514 7.17962 13.3935C7.0781 13.4355 6.98586 13.4971 6.90816 13.5748C6.83046 13.6525 6.76883 13.7448 6.72678 13.8463C6.68473 13.9478 6.66309 14.0566 6.66309 14.1665C6.66309 14.2764 6.68473 14.3852 6.72678 14.4867C6.76883 14.5882 6.83046 14.6805 6.90816 14.7582L9.40816 17.2582C9.48603 17.3354 9.57838 17.3965 9.67991 17.438C9.78144 17.4795 9.89016 17.5005 9.99983 17.4998C10.2144 17.499 10.4203 17.4154 10.5748 17.2665L13.0748 14.8498C13.1633 14.7769 13.2357 14.6864 13.2876 14.5842C13.3394 14.482 13.3697 14.3702 13.3764 14.2557C13.3831 14.1413 13.3661 14.0267 13.3264 13.9191C13.2868 13.8115 13.2254 13.7133 13.1461 13.6305C13.0668 13.5477 12.9712 13.4822 12.8654 13.438C12.7596 13.3939 12.6459 13.372 12.5312 13.3738C12.4166 13.3757 12.3036 13.4011 12.1993 13.4486C12.0949 13.4961 12.0015 13.5646 11.9248 13.6498V13.6498Z" fill="currentColor"/>
      <path d="M14.7252 5.8334C14.3851 4.85094 13.7473 3.99891 12.9004 3.39587C12.0536 2.79282 11.0398 2.46875 10.0002 2.46875C8.96052 2.46875 7.94674 2.79282 7.09988 3.39587C6.25302 3.99891 5.61519 4.85094 5.27516 5.8334C4.52645 5.93368 3.81909 6.23561 3.22874 6.70689C2.63839 7.17818 2.18727 7.80108 1.92363 8.50897C1.65999 9.21687 1.59375 9.98311 1.732 10.7257C1.87024 11.4684 2.20778 12.1595 2.70849 12.7251C2.76897 12.8316 2.8522 12.9235 2.95225 12.9942C3.05229 13.0649 3.1667 13.1127 3.28731 13.1341C3.40793 13.1556 3.53179 13.1501 3.65008 13.1183C3.76836 13.0864 3.87816 13.0288 3.97166 12.9496C4.06515 12.8705 4.14005 12.7717 4.191 12.6603C4.24196 12.5489 4.26773 12.4276 4.26647 12.3051C4.26521 12.1826 4.23696 12.0619 4.18373 11.9515C4.1305 11.8412 4.05359 11.744 3.95849 11.6667C3.63609 11.3063 3.42514 10.8601 3.35123 10.3821C3.27732 9.90423 3.3436 9.41513 3.54205 8.97413C3.7405 8.53312 4.0626 8.15914 4.46932 7.89751C4.87603 7.63587 5.3499 7.49781 5.83349 7.50007H5.91682C6.11178 7.504 6.30194 7.43944 6.45421 7.31763C6.60647 7.19581 6.7112 7.02446 6.75016 6.8334C6.90317 6.07987 7.31198 5.40242 7.90732 4.91581C8.50266 4.42921 9.24792 4.16339 10.0168 4.16339C10.7857 4.16339 11.531 4.42921 12.1263 4.91581C12.7217 5.40242 13.1305 6.07987 13.2835 6.8334C13.3224 7.02446 13.4272 7.19581 13.5794 7.31763C13.7317 7.43944 13.9219 7.504 14.1168 7.50007H14.1668C14.6504 7.49781 15.1243 7.63587 15.531 7.89751C15.9377 8.15914 16.2598 8.53312 16.4583 8.97413C16.6567 9.41513 16.723 9.90423 16.6491 10.3821C16.5752 10.8601 16.3642 11.3063 16.0418 11.6667C15.9684 11.7491 15.9121 11.8451 15.8761 11.9494C15.8401 12.0536 15.8251 12.164 15.8321 12.2741C15.8391 12.3841 15.8679 12.4917 15.9167 12.5906C15.9656 12.6895 16.0336 12.7777 16.1168 12.8501C16.2687 12.984 16.4643 13.0581 16.6668 13.0584C16.7851 13.0582 16.9021 13.0329 17.0098 12.9841C17.1176 12.9352 17.2137 12.864 17.2918 12.7751C17.8064 12.2104 18.1559 11.5152 18.302 10.7653C18.4481 10.0155 18.3853 9.23991 18.1204 8.52338C17.8554 7.80684 17.3986 7.17693 16.7998 6.70251C16.201 6.22809 15.4833 5.92743 14.7252 5.8334V5.8334Z" fill="currentColor"/>
    </g>
    <defs>
      <clipPath id="clip0_906_17595">
        <rect width="20" height="20" fill="white"/>
      </clipPath>
    </defs>
  </svg>
);

const ALL_TABS = [
  { key: "cameras", Icon: GridViewOutlinedIcon, stationaryOnly: true, label: "Камеры комплекса" },
  { key: "directions", Icon: ListOutlinedIcon, nonStationaryOnly: true, label: "Фиксируемые направления" },
  { key: "info", Icon: InfoOutlinedIcon, label: "Подробнее о комплексе" },
  { key: "documents", Icon: DescriptionOutlinedIcon, label: "Документы" },
  { key: "location", Icon: PlaceOutlinedIcon, label: "Расположение комплексов на карте" },
];

const MAIN_WIDTH = 820;
const DIR_WIDTH = 700;
const OFFSET = 10;

const TOOLTIP_PROPS = {
  tooltip: { sx: { backgroundColor: "#000", color: "#fff", fontSize: 12, fontWeight: 500, height: 24, display: "flex", alignItems: "center", px: 1, borderRadius: "4px" } },
  arrow: { sx: { color: "#000" } },
};


const ComplexDrawer = ({ open, item, onClose, initialTab, initialSelectedCamera }) => {
  const classes = useStyles();
  const [activeTab, setActiveTab] = React.useState("cameras");
  const [selectedCamera, setSelectedCamera] = React.useState(null);
  const [docCount, setDocCount] = React.useState(0);

  React.useEffect(() => {
    if (open && item) {
      const isStat = String(item?.class || "").toLowerCase().includes("стационарный");
      setActiveTab(initialTab || (isStat ? "cameras" : "directions"));
      setSelectedCamera(initialSelectedCamera || null);
      setDocCount(0);
    }
  }, [open, item, initialTab, initialSelectedCamera]);

  if (!item) return null;

  const isStationary = String(item?.class || "").toLowerCase().includes("стационарный");
  const tabs = ALL_TABS.filter((t) => {
    if (t.stationaryOnly && !isStationary) return false;
    if (t.nonStationaryOnly && isStationary) return false;
    return true;
  });

  const dirDrawerOpen = Boolean(selectedCamera);

  const titles = { info: "Подробнее о комплексе", documents: "Документы", cameras: "Камеры комплекса", directions: "Фиксируемые направления", location: "Расположение комплексов на карте" };
  const title = titles[activeTab] || "";

  const getBadge = () => {
    if (activeTab === "cameras") return (item?.sensor_cameras || []).length || null;
    if (activeTab === "documents") return docCount || null;
    if (activeTab === "directions") return (item?.sensor_directions || []).reduce((s, d) => s + (d?.directions?.length || 0), 0) || null;
    return null;
  };

  const handleTabClick = (key) => { setActiveTab(key); setSelectedCamera(null); };

  return (
    <>
      <Drawer
        anchor="right"
        open={dirDrawerOpen}
        onClose={() => setSelectedCamera(null)}
        hideBackdrop
        disableScrollLock
        PaperProps={{ className: classes.dirPaper }}
        sx={{ zIndex: "1500 !important", pointerEvents: "none", "& .MuiDrawer-paper": { pointerEvents: "auto", right: "250px !important" } }}
      >
        <DirectionsContent camera={selectedCamera} complexItem={item} onClose={() => setSelectedCamera(null)} />
      </Drawer>

      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{ className: classes.paper }}
        sx={{ "& .MuiDrawer-paper": { right: dirDrawerOpen ? "-600px !important" : `${OFFSET}px !important`, transition: "right 0.3s ease !important" } }}
      >
        <div className={classes.main}>
          <div className={classes.sideRail}>
            {tabs.map(({ key, Icon, label }) => (
              <Tooltip key={key} title={label} placement="right" arrow slotProps={TOOLTIP_PROPS}>
                <IconButton className={clsx(classes.railBtn, activeTab === key && classes.railBtnActive)} onClick={() => handleTabClick(key)} size="small">
                  <Icon sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
            ))}
          </div>

          <div className={classes.content}>
            <div className={classes.header}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography className={classes.title}>{title}</Typography>
                {getBadge() && <span className={classes.badge}>{getBadge()}</span>}
              </Box>
              <IconButton onClick={onClose} size="small">
                <CloseRoundedIcon fontSize="small" />
              </IconButton>
            </div>

            <div className={classes.body}>
              {activeTab === "info" && <TabInfo item={item} />}
              {activeTab === "documents" && <TabDocuments itemId={item?.id} itemName={item?.complex_serial || item?.model} onCountChange={setDocCount} />}
              {activeTab === "cameras" && <TabCameras item={item} selectedSerial={selectedCamera?.cam_serial} onCameraClick={setSelectedCamera} />}
              {activeTab === "directions" && <TabDirectionsView directions={item?.sensor_directions || []} />}
              {activeTab === "location" && <TabLocation item={item} />}
            </div>
          </div>
        </div>
      </Drawer>
    </>
  );
};

const SegmentedTabs = ({ tabs, value, onChange }) => (
  <Box sx={{ display: "inline-flex", backgroundColor: "#F4F4F5", borderRadius: "4px", padding: "4px", mb: 2 }}>
    {tabs.map((tab, idx) => (
      <Box
        key={idx}
        onClick={() => onChange(idx)}
        sx={{
          px: 2, py: "6px", cursor: "pointer", borderRadius: "4px",
          fontSize: 14, fontWeight: 500, lineHeight: "20px", whiteSpace: "nowrap",
          color: value === idx ? "#27272A" : "#52525B",
          backgroundColor: value === idx ? "#fff" : "transparent",
          boxShadow: value === idx ? "0px 1px 2px rgba(24,24,27,0.1), 0px 0px 1px rgba(24,24,27,0.2)" : "none",
          transition: "all 0.15s ease",
          "&:hover": { color: "#27272A" },
        }}
      >
        {tab}
      </Box>
    ))}
  </Box>
);

const DirectionsContent = ({ camera, complexItem, onClose }) => {
  const classes = useStyles();
  const directions = camera?.camera_direction?.directions || [];
  const [selectedIdx, setSelectedIdx] = React.useState(0);
  const [codeTab, setCodeTab] = React.useState(0);
  const cameraAddressesHref = getCameraToAddressesUrl(camera, complexItem);

  React.useEffect(() => { setSelectedIdx(0); setCodeTab(0); }, [camera]);

  const selectedDir = directions[selectedIdx] || null;

  return (
    <div className={classes.dirInner}>
      <div className={classes.header}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1, minWidth: 0 }}>
          <Typography className={classes.title}>Фиксируемые направления</Typography>
          {directions.length > 0 && <span className={classes.badge}>{directions.length}</span>}
          {cameraAddressesHref && (
            <Box sx={{ ml: "auto", mr: 1 }}>
              <LocationGoLink href={cameraAddressesHref} visible />
            </Box>
          )}
        </Box>
        <IconButton onClick={onClose} size="small"><ChevronRightIcon fontSize="small" /></IconButton>
      </div>
      <div className={classes.body}>
        {directions.length === 0 ? (
          <Typography sx={{ fontSize: 14, color: "#71717A", py: 4, textAlign: "center" }}>Нет данных</Typography>
        ) : (
          <>
            <DirectionsTable directions={directions} selectedIdx={selectedIdx} onSelect={(i) => { setSelectedIdx(i); setCodeTab(0); }} />
            {selectedDir && (
              <>
                <SegmentedTabs tabs={["ГОСТ", "Паутина", "Типы нарушений"]} value={codeTab} onChange={setCodeTab} />
                {codeTab === 0 && <Typography sx={{ fontSize: 14, color: "#27272A", lineHeight: "22px" }}>{selectedDir?.gost || "Н/Д"}</Typography>}
                {codeTab === 1 && <Typography sx={{ fontSize: 14, color: "#27272A", lineHeight: "22px" }}>{selectedDir?.duplo || "Н/Д"}</Typography>}
                {codeTab === 2 && <ViolationTypes duploType={selectedDir?.duplo_type} />}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const DirectionsTable = ({ directions, selectedIdx, onSelect }) => (
  <Table size="small" sx={{ border: "1px solid #E4E4E7", borderRadius: "8px", overflow: "hidden", mb: 2 }}>
    <TableHead>
      <TableRow>
        <TableCell>Направление</TableCell>
        <TableCell>Расположение</TableCell>
        <TableCell>Ограничение скорости</TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {directions.map((dir, i) => (
        <TableRow
          key={i}
          onClick={() => onSelect(i)}
          sx={{
            cursor: "pointer",
            "& .MuiTableCell-root": selectedIdx === i ? { backgroundColor: "#EDE9FE !important" } : {},
            "&:hover .MuiTableCell-root": { backgroundColor: selectedIdx === i ? "#EDE9FE" : "#FAFAFA" },
          }}
        >
          <TableCell sx={{ whiteSpace: "nowrap", borderLeft: selectedIdx === i ? "3px solid #4F39F6" : "3px solid transparent" }}>{dir?.direction || "Н/Д"}</TableCell>
          <TableCell>{dir?.location_direction || "Н/Д"}</TableCell>
          <TableCell sx={{ whiteSpace: "nowrap" }}>{dir?.speed_limit ? `${dir.speed_limit} км/ч` : "Н/Д"}</TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

const ViolationTypes = ({ duploType }) => {
  const types = String(duploType || "").split(", ").filter(Boolean);
  if (types.length === 0) return <Typography sx={{ fontSize: 14, color: "#71717A" }}>Н/Д</Typography>;
  return (
    <Box>
      {types.map((v, i) => (
        <Typography key={i} sx={{ fontSize: 14, color: "#27272A", lineHeight: "24px" }}>{i + 1}. {v}</Typography>
      ))}
    </Box>
  );
};

const INFO_SECTIONS = (item, fmt) => [
  { title: "Основная информация", rows: [
    ["Серийный номер", item?.complex_serial],
    ["Модель", item?.model],
    ["Тип средства фиксации", item?.class],
    ["Подтип средства фиксации", item?.subclass],
    ["Дата изготовления", item?.manufacture_date],
    ["Страна производства", item?.complex_country],
    ["Производитель", item?.complex_producer],
    ["Количество видеокамер", item?.cam_count],
  ]},
  { title: "Местоположение и размещение", rows: [
    ["Месторасположение", getComplexLocationLabel(item)],
    ["Координаты комплекса", item?.latitude_complex && item?.longitude_complex ? `${item.latitude_complex}, ${item.longitude_complex}` : null],
    ["Регион РФ", item?.region],
    ["Значение автодороги", item?.road_class],
    ["Муниципальное образование", item?.municipality],
    ["Категория места расположения", item?.location_cat],
    ["Обоснование категории", item?.location_cat_reason],
    ["ОКТМО", item?.oktmo],
  ]},
  { title: "Дорожные условия и зоны контроля", rows: [
    ["Расстояние до знака 6.22", item?.distance_622],
    ["Расстояние до знака 5.23", item?.distance_523],
    ["Наличие общих зон контроля", item?.shared_zones],
    ["Расстояние до ближайшего средства", item?.complex_distance],
    ["Ширина зоны контроля", item?.control_zone_width ? `${item.control_zone_width} м` : null],
    ["Длина зоны контроля", item?.control_zone_length ? `${item.control_zone_length} м` : null],
    ["Количество контролируемых полос", item?.control_zone_lanes],
  ]},
  { title: "Эксплуатационная информация", rows: [
    ["Статус работы", item?.commisioning_status],
    ["Дата изменения статуса", fmt(item?.status_change_date)],
    ["Основание изменения статуса", item?.status_change_reason],
    ["Дата ввода в Паутину", fmt(item?.starting_date)],
    ["Дата начала оказания услуг", fmt(item?.service_start)],
    ["Работоспособность", item?.online != null && item?.online !== "" ? (item.online ? "Работает" : "Не работает") : null],
    ["Сведения о применении в Паутине", item?.commisioning_info],
    ["Дата письма ГИБДД", fmt(item?.commissioning_date)],
  ]},
  { title: "Разрешительная документация ИЗ СИСТЕМЫ УЧЕТА", rows: [
    ["Номер свидетельства СИ", item?.license_num],
    ["Начало действия свидетельства", fmt(item?.license_start_date)],
    ["Окончание действия свидетельства", fmt(item?.license_end_date)],
    ["Номер поверки", item?.verification_num],
    ["Дата очередной поверки", fmt(item?.verification_outdata)],
  ]},
  { title: 'Разрешительная документация ИЗ ФГИС "Аршин"', rows: item?.certificate_arshin ? [
    ["Номер свидетельства СИ", item.certificate_arshin.mit_number],
    ["Начало действия свидетельства", fmt(item.certificate_arshin.verification_date)],
    ["Окончание действия свидетельства", fmt(item.certificate_arshin.valid_date)],
    ["Номер поверки", item.certificate_arshin.result_docnum],
    ["Дата очередной поверки", fmt(item.certificate_arshin.valid_date)],
  ] : [] },
  { title: "Владельцы и обслуживание", rows: [
    ["Собственник", item?.owner],
    ["Обслуживающая организация", item?.servorg],
    ["Согласование с владельцем", item?.owner_alignment_doc],
    ["Дата согласования", fmt(item?.owner_alignment_date)],
  ]},
  { title: "Интеграция с ГИБДД", rows: [
    ["Место расположения в приказе", item?.location_gibdd || "тот же"],
    ["Основание изменения адреса", item?.location_change_reason],
  ]},
  { title: "Демонтаж", rows: [
    ["Год демонтажа", fmt(item?.demontage_date || "")],
    ["Причина демонтажа", item?.demontage_reason],
  ]},
  { title: "Финансовая информация", rows: [
    ["Стоимость СМР", item?.smr_price ? `${item.smr_price} тысяч рублей` : null],
    ["Признак национального проекта", item?.bkd || "Не БКД"],
  ]},
];

const InfoRow = ({ label, value, href, classes }) => (
  <div className={classes.row}>
    <span className={classes.label}>{label}</span>
    {href ? (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, minWidth: 0, flex: 1 }}>
        <span className={classes.value}>{value || "Н/Д"}</span>
        <LocationGoLink href={href} visible />
      </Box>
    ) : (
      <span className={classes.value}>{value || "Н/Д"}</span>
    )}
  </div>
);

const TabInfo = ({ item }) => {
  const classes = useStyles();
  const sections = INFO_SECTIONS(item, formatDate);
  const locationHref = getSensorToAddressesUrl(item);

  return (
    <Box>
      {sections.map((section, idx) => (
        <Accordion key={idx} disableGutters elevation={0} className={classes.accordion}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.accordionSummary}>
            <Typography className={classes.accordionTitle}>{section.title}</Typography>
          </AccordionSummary>
          <AccordionDetails className={classes.accordionDetails}>
            {section.rows.length > 0 ? section.rows.map(([label, value], i) => (
              label === "Месторасположение" ? (
                <InfoRow key={i} label={label} value={value} href={locationHref} classes={classes} />
              ) : (
                <div key={i} className={classes.row}>
                  <span className={classes.label}>{label}</span>
                  <span className={classes.value}>{value || "Н/Д"}</span>
                </div>
              )
            )) : (
              <Typography sx={{ fontSize: 14, color: "#71717A" }}>Нет данных</Typography>
            )}
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

const TabCameras = ({ item, selectedSerial, onCameraClick }) => {
  const cameras = item?.sensor_cameras || [];
  if (cameras.length === 0) {
    return <Typography sx={{ fontSize: 14, color: "#71717A", py: 4, textAlign: "center" }}>Нет данных о камерах</Typography>;
  }
  return (
    <Table size="small" sx={{ border: "1px solid #E4E4E7", borderRadius: "8px", overflow: "hidden" }}>
      <TableHead>
        <TableRow>
          <TableCell>Серийный номер</TableCell>
          <TableCell>Местоположение</TableCell>
          <TableCell>Координаты</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {cameras.map((cam, idx) => {
          const isSelected = selectedSerial === cam?.cam_serial;
          const cameraAddressesHref = getCameraToAddressesUrl(cam, item);
          const locationLabel = getCameraLocationLabel(cam) || getComplexLocationLabel(item);
          return (
            <TableRow
              key={idx}
              hover
              onClick={() => onCameraClick(cam)}
              sx={{
                cursor: "pointer",
                "& .MuiTableCell-root": isSelected ? { backgroundColor: "#EDE9FE !important" } : {},
                "&.MuiTableRow-hover:hover .MuiTableCell-root": { backgroundColor: isSelected ? "#EDE9FE" : "#FAFAFA" },
              }}
            >
              <TableCell sx={{ whiteSpace: "nowrap", borderLeft: isSelected ? "3px solid #4F39F6" : "3px solid transparent" }}>{cam?.cam_serial}</TableCell>
              <TableCell>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                  <span>{formatAddressDisplay(locationLabel)}</span>
                  {cameraAddressesHref && (
                    <IconGoLink href={cameraAddressesHref} visible />
                  )}
                </Box>
              </TableCell>
              <TableCell sx={{ whiteSpace: "nowrap" }}>{[cam?.latitude_cam, cam?.longitude_cam].filter(Boolean).join(", ") || "Н/Д"}</TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};

const TabDirectionsView = ({ directions: sensorDirections }) => {
  const allDirs = sensorDirections.flatMap((sd) => sd?.directions || []);
  const [codeTab, setCodeTab] = React.useState(0);

  if (allDirs.length === 0) {
    return <Typography sx={{ fontSize: 14, color: "#71717A", py: 4, textAlign: "center" }}>Нет данных</Typography>;
  }
  return (
    <Box>
      <DirectionsTable directions={allDirs} selectedIdx={-1} onSelect={() => {}} />
      <SegmentedTabs tabs={["ГОСТ", "Паутина", "Типы нарушений"]} value={codeTab} onChange={setCodeTab} />
      {codeTab === 0 && <Typography sx={{ fontSize: 14, color: "#27272A", lineHeight: "22px" }}>{allDirs.map((d) => d?.gost).filter(Boolean).join(", ") || "Н/Д"}</Typography>}
      {codeTab === 1 && <Typography sx={{ fontSize: 14, color: "#27272A", lineHeight: "22px" }}>{allDirs.map((d) => d?.duplo).filter(Boolean).join(", ") || "Н/Д"}</Typography>}
      {codeTab === 2 && <ViolationTypes duploType={allDirs.map((d) => d?.duplo_type).filter(Boolean).join(", ")} />}
    </Box>
  );
};

const TabDocuments = ({ itemId, itemName, onCountChange }) => {
  const [documents, setDocuments] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const loadedRef = React.useRef(null);

  React.useEffect(() => {
    if (!itemId || loadedRef.current === itemId) return;
    loadedRef.current = itemId;
    setIsLoading(true);
    const params = new URLSearchParams({
      linked_entity_id: itemId,
      hide_from_user: "false"
    });
    // if (itemName) params.set("linked_entity_name", itemName);
    agent.get(`/documents?${params.toString()}`)
      .then((res) => { const docs = res?.data?.data || []; setDocuments(docs); onCountChange?.(docs.length); })
      .catch(() => { setDocuments([]); onCountChange?.(0); })
      .finally(() => setIsLoading(false));
  }, [itemId, onCountChange]);

  if (isLoading) return <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress size={28} /></Box>;
  if (documents.length === 0) return <Typography sx={{ fontSize: 14, color: "#71717A", py: 4, textAlign: "center" }}>Документы не найдены</Typography>;

  return (
    <Table size="small" sx={{ border: "1px solid #E4E4E7", borderRadius: "8px", overflow: "hidden" }}>
      <TableHead>
        <TableRow>
          <TableCell sx={{ minWidth: 200 }}>Название</TableCell>
          <TableCell>Тип документа</TableCell>
          <TableCell>Дата загрузки</TableCell>
          <TableCell sx={{ width: 48 }} />
        </TableRow>
      </TableHead>
      <TableBody>
        {documents.map((doc, idx) => {
          const FileIcon = getFileTypeIcon(doc.format);
          return (
            <TableRow key={doc.id || idx} hover>
              <TableCell sx={{ maxWidth: 260 }}>
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                  <FileIcon sx={{ fontSize: 20, flexShrink: 0, mt: 0.25 }} />
                  <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0, maxWidth: 220 }}>
                    <span style={{ fontSize: 14, color: "#27272A", fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "block" }}>
                      {truncateFilename(doc.original_name, 30)}
                    </span>
                    {doc.size > 0 && <span style={{ fontSize: 12, color: "#71717A", lineHeight: "16px" }}>{formatFileSize(doc.size)}</span>}
                  </Box>
                </Box>
              </TableCell>
              <TableCell sx={{ fontSize: 14, color: "#71717A" }}>{doc.type_name || "Н/Д"}</TableCell>
              <TableCell sx={{ fontSize: 14, whiteSpace: "nowrap" }}>{formatDate(doc.created)}</TableCell>
              <TableCell>
                {doc.url && (
                  <IconButton size="small" sx={{ color: "#A1A1AA", p: 0.5 }} onClick={(e) => { e.stopPropagation(); downloadFile(doc.url, doc.original_name); }}>
                    <CloudDownloadIcon />
                  </IconButton>
                )}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};

const TabLocation = ({ item }) => {
  const classes = useStyles();
  const lat = parseFloat(item?.latitude_complex);
  const lng = parseFloat(item?.longitude_complex);
  const hasCoords = !isNaN(lat) && !isNaN(lng);
  const coords = hasCoords ? `${lat}, ${lng}` : null;
  const locationHref = getSensorToAddressesUrl(item);
  const locationLabel = getComplexLocationLabel(item);

  return (
    <Box>
      <InfoRow label="Адрес" value={locationLabel} href={locationHref} classes={classes} />
      <div className={classes.row}><span className={classes.label}>Координаты</span><span className={classes.value}>{coords || "Н/Д"}</span></div>
      <div className={classes.row}><span className={classes.label}>Регион</span><span className={classes.value}>{item?.region || "Н/Д"}</span></div>
      {hasCoords && (
        <Box sx={{ mt: 2, borderRadius: "8px", overflow: "hidden", border: "1px solid #E4E4E7", height: 350 }}>
          <LocationMap lat={lat} lng={lng} label={locationLabel || item?.complex_serial} />
        </Box>
      )}
    </Box>
  );
};

const LocationMap = React.memo(({ lat, lng, label }) => {
  const { MapContainer, TileLayer, Marker, Popup } = require("react-leaflet");
  const L = require("leaflet");

  const icon = L.divIcon({
    className: "",
    html: '<svg width="32" height="40" viewBox="0 0 32 40" fill="none"><path d="M16 0C7.164 0 0 7.164 0 16c0 12 16 24 16 24s16-12 16-24C32 7.164 24.836 0 16 0z" fill="#4F39F6"/><circle cx="16" cy="16" r="6" fill="white"/></svg>',
    iconSize: [32, 40],
    iconAnchor: [16, 40],
    popupAnchor: [0, -40],
  });

  return (
    <MapContainer center={[lat, lng]} zoom={12} style={{ height: "100%", width: "100%" }} scrollWheelZoom attributionControl={false}>
      <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
      <Marker position={[lat, lng]} icon={icon}>
        {label && <Popup>{label}</Popup>}
      </Marker>
    </MapContainer>
  );
});

const useStyles = makeStyles(() => ({
  paper: {
    maxWidth: "100%",
    borderLeft: "1px solid #E4E4E7",
    zIndex: "1400 !important",
    boxShadow: "-8px 0 24px -4px rgba(0, 0, 0, 0.08)",
    width: "100%",
    "@media (min-width: 600px)": { width: MAIN_WIDTH, top: 12, height: "calc(100% - 24px)", borderRadius: 10 },
  },
  dirPaper: {
    maxWidth: "100%",
    borderLeft: "1px solid #E4E4E7",
    zIndex: "1300 !important",
    boxShadow: "-8px 0 24px -4px rgba(0, 0, 0, 0.08)",
    width: "100%",
    "@media (min-width: 600px)": { width: DIR_WIDTH, top: 12, height: "calc(100% - 24px)", borderRadius: 10 },
  },
  dirInner: { display: "flex", flexDirection: "column", height: "100%" },
  main: { display: "flex", height: "100%" },
  sideRail: {
    width: 48, flexShrink: 0, borderRight: "1px solid #E4E4E7",
    backgroundColor: "#FAFAFA", display: "flex", flexDirection: "column",
    alignItems: "center", paddingTop: 12, gap: 4,
  },
  railBtn: {
    width: 36, height: 36, borderRadius: 8, color: "#71717A",
    "&:hover": { backgroundColor: "#F4F4F5", color: "#27272A" },
  },
  railBtnActive: { backgroundColor: "#E0E7FF !important", color: "#432DD7 !important" },
  content: { flex: 1, display: "flex", flexDirection: "column", minWidth: 0, overflow: "hidden" },
  header: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "20px 24px 16px", flexShrink: 0 },
  title: { fontSize: 18, fontWeight: 600, color: "#27272A", lineHeight: "24px" },
  badge: { fontSize: 12, fontWeight: 500, color: "#432DD7", backgroundColor: "#E0E7FF", borderRadius: 16, padding: "2px 8px" },
  body: { flex: 1, overflow: "auto", padding: "0 12px 24px" },
  accordion: { borderBottom: "1px solid #E4E4E7", "&:before": { display: "none" }, "&.Mui-expanded": { margin: 0 } },
  accordionSummary: { padding: "0 !important", minHeight: "48px !important", "& .MuiAccordionSummary-content": { margin: "12px 0 !important" } },
  accordionTitle: { fontSize: 14, fontWeight: 600, color: "#27272A", lineHeight: "20px" },
  accordionDetails: { padding: "0 0 16px 0 !important" },
  row: { display: "flex", alignItems: "flex-start", gap: 16, padding: "6px 0" },
  label: { width: 180, flexShrink: 0, fontSize: 14, color: "#71717A", lineHeight: "20px" },
  value: { flex: 1, fontSize: 14, color: "#27272A", lineHeight: "20px", minWidth: 0 },
  emptyText: { fontSize: 14, color: "#71717A", padding: "32px 0", textAlign: "center" },
  tableWrap: { border: "1px solid #E4E4E7", borderRadius: 8, overflow: "hidden", marginBottom: 16 },
  mapWrap: { marginTop: 16, borderRadius: 8, overflow: "hidden", border: "1px solid #E4E4E7", height: 350 },
  codeText: { fontSize: 14, color: "#27272A", lineHeight: "22px" },
  docNameCell: { maxWidth: 260 },
  docNameWrap: { display: "flex", alignItems: "flex-start", gap: 8 },
  docFileIcon: { fontSize: "20px !important", flexShrink: 0, marginTop: 2 },
  docNameInfo: { display: "flex", flexDirection: "column", minWidth: 0, maxWidth: 220 },
  docFileName: { fontSize: 14, color: "#27272A", fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "block" },
  docFileSize: { fontSize: 12, color: "#71717A", lineHeight: "16px" },
  docType: { fontSize: 14, color: "#71717A" },
  docDate: { fontSize: 14, whiteSpace: "nowrap" },
  docDownloadBtn: { color: "#4F39F6", padding: 4 },
  selectedRow: {
    cursor: "pointer",
    "& .MuiTableCell-root": { backgroundColor: "#EDE9FE !important" },
    "&:hover .MuiTableCell-root": { backgroundColor: "#EDE9FE" },
  },
  selectedDocRow: {
    cursor: "pointer",
    "& .MuiTableCell-root": { backgroundColor: "#E0E7FF !important" },
    "& .MuiTableCell-root:first-of-type": { borderLeft: "4px solid #432DD7" },
  },
  unselectedDocRow: {
    cursor: "pointer",
    "& .MuiTableCell-root:first-of-type": { borderLeft: "4px solid transparent" },
    "&:hover .MuiTableCell-root": { backgroundColor: "#FAFAFA" },
  },
}));

export default ComplexDrawer;
